/** [lon, lat] from "lon,lat", a JSON "[lon,lat]" or an array. Null when unusable. */
export function parseLonLat(value: unknown): [number, number] | null {
  let v = value;
  if (typeof v === "string") v = v.trim().startsWith("[") ? safeJson(v) : v.split(",");
  if (!Array.isArray(v) || v.length < 2) return null;
  const lon = Number(v[0]);
  const lat = Number(v[1]);
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || Math.abs(lon) > 180 || Math.abs(lat) > 90) return null;
  if (lon === 0 && lat === 0) return null;
  return [lon, lat];
}

function safeJson(s: string): unknown {
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

type FlightRow = { flightId?: unknown; deleted?: unknown; actArrTime?: unknown };

/** The flights that are neither deleted nor landed. */
export function openFlights<T extends FlightRow>(rows: T[]): T[] {
  return rows.filter((r) => Number(r.deleted) !== 1 && !(Number(r.actArrTime) > 0));
}

/** The newest flight that is neither deleted, landed nor over. Rows come oldest
 *  first. `over` holds the ids of open flights the flow briefing no longer shows:
 *  a flight with no landing time must not count as active for a day. */
export function activeFlight<T extends FlightRow>(rows: T[], over: Set<string> = new Set()): T | undefined {
  return openFlights(rows)
    .reverse()
    .find((r) => !over.has(String(r.flightId)));
}

/** True when the mission's own flight has landed or is over. `rows` are the rows
 *  of that mission's flight ids only; another flight's landing must never end a
 *  mission. */
export function missionFlown(rows: FlightRow[], over: Set<string> = new Set()): boolean {
  return rows.some((r) => Number(r.deleted) !== 1 && (Number(r.actArrTime) > 0 || over.has(String(r.flightId))));
}

/** The ids a mission's flight can have, built as `focMain.missionFlightSlot`
 *  builds them: md5(reg|mission|id), then |1, |2 after each deleted flight.
 *  Each registration counts as written and in upper case, because the create
 *  and this view read it from different places.
 *  ponytail: the first 5 slots; a mission flight deleted 5 times is not found. */
export function missionFlightIds(regs: unknown[], missionId: number, md5: (s: string) => string): Set<string> {
  const ids = new Set<string>();
  for (const raw of regs) {
    if (!raw) continue;
    for (const reg of [String(raw), String(raw).toUpperCase()]) {
      for (let n = 0; n < 5; n++) {
        ids.add(md5(reg + "|mission|" + missionId + (n > 0 ? "|" + n : "")).substring(0, 20));
      }
    }
  }
  return ids;
}

/** The mission the view shows: its id and its text. */
export interface ShownMission {
  id: string;
  body: string;
}

export type WallboardMessage =
  { type: "MISSION_STARTED" | "MISSION_UPDATED"; mission: ShownMission } | { type: "MISSION_ENDED" };

/** The message that tells the wallboard what the view shows now, or null when the
 *  wallboard already knows. `sent` is undefined before the first message, so the
 *  view always tells a (re)loaded wallboard its state once. A new id with the same
 *  text sends nothing: the flight created from a mission carries that mission's text. */
export function nextWallboardMessage(
  sent: ShownMission | null | undefined,
  now: ShownMission | null
): WallboardMessage | null {
  if (!now) return sent === null ? null : { type: "MISSION_ENDED" };
  if (sent && sent.body === now.body) return null;
  return { type: sent && sent.id === now.id ? "MISSION_UPDATED" : "MISSION_STARTED", mission: now };
}

/** What one pass read: the active flight, the latest mission of the base, and
 *  whether that mission's own flight has landed. */
export interface ViewRead {
  flight: { id: string; text: string; summary?: string; dest: [number, number] | null; failed?: boolean };
  listFailed: boolean;
  last?: { id: number; body: string; destination?: string; timestamp: string };
  flown: boolean;
  /** The flight shown is the own flight of `last`. */
  flightOfLast?: boolean;
}

/** What the view shows after a pass, and what the wallboard is told. */
export interface ViewDecision {
  /** No active flight and no open mission: the view shows the base. */
  waiting: boolean;
  body: string;
  shown: ShownMission | null;
  dest: [number, number] | null;
  destLabel: string;
  lastMission: Date | null;
  /** When the alarm came: the time of the mission shown, or of the mission the
   *  flight shown was created from. Null for a flight made by hand: a flight's
   *  `creationDate` is no clock time (focMain shifts it by the UTC offset). */
  alarm: Date | null;
}

/** The text for a flight that carries no alarm text: its route and planned times.
 *  Empty when the flight has neither a route name nor a time. */
export function flightSummary(origin: unknown, dest: unknown, etdMs: unknown, etaMs: unknown): string {
  const hhmm = (ms: unknown): string => {
    const d = new Date(Number(ms));
    if (!(Number(ms) > 0) || isNaN(d.getTime())) return "";
    return String(d.getHours()).padStart(2, "0") + ":" + String(d.getMinutes()).padStart(2, "0");
  };
  const route = [origin, dest]
    .map((s) => String(s ?? "").trim())
    .filter(Boolean)
    .join(" → ");
  const times = [
    ["ETD", hhmm(etdMs)],
    ["ETA", hhmm(etaMs)],
  ]
    .filter(([, t]) => t)
    .map(([k, t]) => k + " " + t)
    .join(" · ");
  if (!route && !times) return "";
  return ["Flight" + (route ? " " + route : ""), times].filter(Boolean).join("\n");
}

/** "15 s" or "3 min": how old the shown data is. */
export function dataAge(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  return s < 120 ? s + " s" : Math.floor(s / 60) + " min";
}

/** Decides the view from one pass, or returns null when a read failed. The
 *  flights and the mission list come from two backends, and either read can fail
 *  alone. A pass decides only from two answers of the same time: after a failed
 *  read the view and the wallboard keep their state.
 *  The active flight wins: a flight created from a mission keeps the mission text
 *  (opsRemarks) and its destination, synced from the pilot's device. A mission
 *  whose own flight has landed is over. */
export function decideView(read: ViewRead): ViewDecision | null {
  const { flight, last } = read;
  if (flight.failed || read.listFailed) return null;
  const mission = last && !read.flown ? last : undefined;
  // A flight made by hand has no alarm text. Its summary stands in, so the
  // wallboard treats it as a mission; an open mission's text still wins.
  // A flight whose route and times have not arrived yet still counts: a bare
  // "Flight" keeps the wallboard out of WAITING until the summary follows.
  const body = flight.text || mission?.body || flight.summary || (flight.id ? "Flight" : "");
  const missionDest = parseLonLat(mission?.destination);
  return {
    waiting: !flight.id && !mission,
    body,
    shown:
      flight.text || (!mission && flight.id)
        ? { id: "flight:" + flight.id, body }
        : mission
          ? { id: String(mission.id), body }
          : null,
    dest: flight.dest ?? missionDest,
    destLabel: flight.dest ? "Flight destination" : missionDest ? "Mission destination" : "No destination",
    lastMission: last ? new Date(last.timestamp) : null,
    alarm: last && ((flight.id && read.flightOfLast) || (!flight.text && !!mission)) ? new Date(last.timestamp) : null,
  };
}
